import * as FluxUtils from 'flux/utils';

import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { PersistentStore } from './PersistentStore';
import { AppConfiguration } from './AppConfigurationStore';
import { ProjectSettingsStore } from './ProjectSettingsStore';

import utils = egr.wcf.utils;

interface PriceState {
    exchangeRates: Map<string, number>;
}

class PriceStoreClass extends FluxUtils.ReduceStore<PriceState, unknown> {
    public getInitialState(): PriceState {
        return {
            exchangeRates: AppConfiguration.DEFAUL_CURRENCY_MAP
        };
    }

    /**
     * Returns the price in the global currency.
     * @param  {number} itemPrice    Price of the item (in item currency)
     * @param  {string} itemCurrency Currency of the item
     * @return {number}              Price in global currency
     */
    public getPrice(itemPrice: number, itemCurrency: string): number {
        const exchangeRate: number | undefined = this.getExchangeRate(itemCurrency);
        if (exchangeRate == null) {
            return itemPrice;
        }
        return exchangeRate * itemPrice;
    }

    /**
     * Returns string of price in global currency with correct localization.
     */
    public getPriceString(itemPrice: number, itemCurrency: string): string {
        const currencySetting: Intl.NumberFormatOptions = { style: 'currency', currency: ProjectSettingsStore.getState().currency };
        if (
            currencySetting.currency !== 'EUR' &&
            currencySetting.currency !== 'GBP' &&
            currencySetting.currency !== 'USD' &&
            currencySetting.currency !== 'JPY'
        ) {
            currencySetting.currencyDisplay = 'code';
        }
        return this.getPrice(itemPrice, itemCurrency).toLocaleString(PersistentStore.getState().language || 'en', currencySetting);
    }

    /**
     * Checks if the currency of an article item is in the exchange rate list from the eaiws.
     */
    public isCurrencyValid(currency: string | null | undefined): boolean {
        if (utils.string.isNullOrEmpty(currency)) {
            return false;
        }
        return this.getState().exchangeRates.has(currency);
    }

    public reduce(state: PriceState, action: DispatcherBaseAction): PriceState {
        // Note: this function should be `pure` -> we have to return a copy
        // of the current state if some property was changed
        switch (action.actionType) {
            case ActionConstants.UPDATE_EXCHANGE_RATES:
                return jQuery.extend(
                    {},
                    state,
                    { exchangeRates: action.value }
                );
            default:
                break;
        }

        return state;
    }

    /**
     * Returns exchange rate to convert the price to the global defined (in PriceStore) currency.
     * @param  {string} itemCurrency Currency of the article item
     * @return {number}              Returns 0 if currency is not in eaiws currency list!
     */
    private getExchangeRate(itemCurrency: string): number | undefined {
        if (itemCurrency === ProjectSettingsStore.getState().currency) {
            return 1;
        }
        if (itemCurrency !== 'EUR') {
            const itemCurrencyExchangeRate: number | undefined = this.getState().exchangeRates.get(itemCurrency);
            if (itemCurrencyExchangeRate == null) {
                return 0;
            }
            const targetCurrencyExchangeRate: number | undefined = this.getState().exchangeRates.get(ProjectSettingsStore.getState().currency);
            if (targetCurrencyExchangeRate == null) {
                return 0;
            }
            return targetCurrencyExchangeRate / itemCurrencyExchangeRate;
        }
        return this.getState().exchangeRates.get(ProjectSettingsStore.getState().currency);
    }
}

export let PriceStore: PriceStoreClass = new PriceStoreClass(AppDispatcher);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/stores/PriceStore.ts